import {
  FaCertificate,
  FaCoffee,
  FaCompactDisc,
  FaImage,
  FaKey,
  FaShoppingBag,
  FaTag,
  FaTshirt,
} from "react-icons/fa";
import {
  FiTrendingUp,
  FiMusic,
  FiMic,
  FiDisc,
  FiVideo,
  FiUsers,
} from "react-icons/fi";
import { GiHoodie, GiMonclerJacket } from "react-icons/gi";
import { PiBaseballCapBold } from "react-icons/pi";

export const capitalizeWords = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Kompaktan opseg za paginaciju: 1 … (current-1) current (current+1) … last
// Vraća niz brojeva strana sa "..." stringovima gde se preskaču strane.
export const getPaginationRange = (current, total, siblings = 1) => {
  const totalNumbers = siblings * 2 + 5; // prva, poslednja, trenutna + 2 "..."
  if (total <= totalNumbers) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, total);

  const showLeftDots = left > 2;
  const showRightDots = right < total - 1;

  const range = [];

  if (!showLeftDots && showRightDots) {
    const leftItemCount = 3 + 2 * siblings;
    for (let i = 1; i <= leftItemCount; i++) range.push(i);
    range.push("...");
    range.push(total);
  } else if (showLeftDots && !showRightDots) {
    range.push(1);
    range.push("...");
    const rightItemCount = 3 + 2 * siblings;
    for (let i = total - rightItemCount + 1; i <= total; i++) range.push(i);
  } else {
    range.push(1);
    range.push("...");
    for (let i = left; i <= right; i++) range.push(i);
    range.push("...");
    range.push(total);
  }

  return range;
};

// Skrati tekst tako što zameni poslednja 3 karaktera sa "..."
export const truncateWithEllipsis = (text, maxLength = 30) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;

  // Ukloni poslednja 3 karaktera i dodaj "..."
  return text.slice(0, maxLength - 3) + "...";
};

export const formatPrice = (price, eur = false) => {
  if (!price) return eur ? "0 €" : "0 RSD";

  // Cene su celi brojevi, bez ",0" decimala (v2 preduslov: "1.800 RSD")
  if (eur) {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  } else {
    return new Intl.NumberFormat("sr-Latn-RS", {
      style: "currency",
      currency: "RSD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }
};

export const formatNewsDetailsDate = (date) => {
  if (!date) return "";
  const today = new Date();
  const newsDate = new Date(date);
  if (isNaN(newsDate.getTime())) return "";
  const diffTime = newsDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Danas";
  if (diffDays === 1) return "Sutra";
  if (diffDays === -1) return "Juče";
  if (diffDays > 0 && diffDays <= 7) return `Za ${diffDays} dana`;
  if (diffDays < 0 && diffDays >= -7) return `Pre ${Math.abs(diffDays)} dana`;

  return newsDate.toLocaleDateString("sr-Latn-RS", {
    day: "numeric",
    month: "long",
    year:
      newsDate.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("sr-Latn-RS", { month: "long" });
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day}. ${month} ${year}, ${hours}:${minutes}h`;
};

export const formatDatePeriod = (startDateString, endDateString) => {
  const startDate = new Date(startDateString);
  const endDate = new Date(endDateString);

  const startDay = startDate.getDate();
  const endDay = endDate.getDate();

  // mesec i godina iz krajnjeg datuma; opseg sa en-dash (v2 s.10.2)
  const month = endDate.toLocaleString("sr-Latn-RS", { month: "long" });
  const year = endDate.getFullYear();

  return `${startDay}–${endDay}. ${month} ${year}.`;
};

export const formatDateNoHours = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("sr-Latn-RS", { month: "long" });
  const year = date.getFullYear();

  return `${day}. ${month} ${year}.`;
};

export const getBadgeLabel = (product) => {
  if (product.salePrice && product.price) {
    const discount = Math.round(
      ((product.price - product.salePrice) / product.price) * 100,
    );
    return `-${discount}%`;
  }
  if (product.isNew) return "Novo";
  if (product.isLimited) return "Limited";
  if (product.isBestSeller) return "Bestseller";
  return "";
};

export const getVariant = (product) => {
  if (product.salePrice && product.price) {
    return "discount";
  }
  if (product.isNew) return "novo";
  if (product.isLimited) return "limited";
  if (product.isBestSeller) return "bestseller";
  return "";
};

export const getNewsTypeLabel = (type) => {
  const typeMap = {
    concert: "Koncert",
    album: "Album",
    interview: "Intervju",
    video: "Video",
    award: "Nagrada",
    festival: "Festival",
  };
  return typeMap[type] || type;
};

export const getNewsTypeIcon = (type) => {
  const iconMap = {
    concert: FiMusic,
    album: FiDisc,
    interview: FiMic,
    video: FiVideo,
    award: FiTrendingUp,
    festival: FiUsers,
  };
  return iconMap[type] || FiMusic;
};
export const newsTypes = [
  { id: 1, name: "Koncert", icon: FiMusic, value: "concert" },
  { id: 2, name: "Album", icon: FiDisc, value: "album" },
  { id: 3, name: "Intervju", icon: FiMic, value: "interview" },
  { id: 4, name: "Video", icon: FiVideo, value: "video" },
  { id: 5, name: "Nagrada", icon: FiTrendingUp, value: "award" },
  { id: 6, name: "Festival", icon: FiUsers, value: "festival" },
];
export const formatNewsDate = (date) => {
  // Novost ne mora da ima datum (npr. festival/album bez konkretnog termina)
  if (!date) return "";

  const today = new Date();

  // Ako je string (iz inputa), konvertuj u Date
  const d = typeof date === "string" ? new Date(date) : date;

  // Zaštita od nevalidnog datuma da se stranica ne sruši
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";

  const diffTime = d - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Danas";
  if (diffDays === 1) return "Sutra";
  if (diffDays === -1) return "Juče";
  if (diffDays > 0 && diffDays <= 7) return `Za ${diffDays} dana`;
  if (diffDays < 0 && diffDays >= -7) return `Pre ${Math.abs(diffDays)} dana`;

  return d.toLocaleDateString("sr-Latn-RS", {
    day: "numeric",
    month: "short",
    year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
};

export const categoryMapping = {
  majica: {
    icon: FaTshirt,
    name: "Majice",
  },
  duks: {
    icon: GiHoodie,
    name: "Duksevi",
  },
  "vinil & cd": {
    icon: FaCompactDisc,
    name: "Vinili & CD",
  },
  vinil: {
    icon: FaCompactDisc,
    name: "Vinili",
  },
  cd: {
    icon: FaCompactDisc,
    name: "CD",
  },
  jakna: {
    icon: GiMonclerJacket,
    name: "Jakne",
  },
  kačket: {
    icon: PiBaseballCapBold,
    name: "Kačketi",
  },
  nalepnica: {
    icon: FaTag,
    name: "Nalepnice",
  },
  poster: {
    icon: FaImage,
    name: "Posteri",
  },
  privezak: {
    icon: FaKey,
    name: "Privesci",
  },
  šolja: {
    icon: FaCoffee,
    name: "Šolje",
  },
  torba: {
    icon: FaShoppingBag,
    name: "Torbe",
  },
  značka: {
    icon: FaCertificate,
    name: "Značke",
  },
  bedž: {
    icon: FaCertificate,
    name: "Bedževi",
  },
  aksesoari: {
    icon: FaShoppingBag,
    name: "Aksesoari",
  },
};

export const getProductSizeStocks = (product) => {
  const stocks = [];
  if (product.sizeXS && product.stockSizeXS > 0)
    stocks.push(`XS: ${product.stockSizeXS}`);
  if (product.sizeS && product.stockSizeS > 0)
    stocks.push(`S: ${product.stockSizeS}`);
  if (product.sizeM && product.stockSizeM > 0)
    stocks.push(`M: ${product.stockSizeM}`);
  if (product.sizeL && product.stockSizeL > 0)
    stocks.push(`L: ${product.stockSizeL}`);
  if (product.sizeXL && product.stockSizeXL > 0)
    stocks.push(`XL: ${product.stockSizeXL}`);
  if (product.sizeXXL && product.stockSizeXXL > 0)
    stocks.push(`XXL: ${product.stockSizeXXL}`);
  if (product.size3XL && product.stockSize3XL > 0)
    stocks.push(`3XL: ${product.stockSize3XL}`);
  if (product.size4XL && product.stockSize4XL > 0)
    stocks.push(`4XL: ${product.stockSize4XL}`);
  if (product.size5XL && product.stockSize5XL > 0)
    stocks.push(`5XL: ${product.stockSize5XL}`);
  return stocks;
};

export const getProductSizeStocksWithAlerts = (product) => {
  const stocks = [];

  // Check if product has no sizes (noSize products)
  if (product.noSize) {
    const alertThreshold = product.stockAlert || 10;
    const stock = product.stock || 0;
    let alertLevel = "normal";

    if (stock < alertThreshold) {
      alertLevel = "critical"; // Red
    } else if (stock < alertThreshold * 2) {
      alertLevel = "warning"; // Orange
    }

    stocks.push({
      size: "Ukupno",
      stock,
      alertThreshold,
      alertLevel,
      displayText: `Ukupno: ${stock}`,
    });

    return stocks;
  }

  // Handle products with sizes
  const sizeConfig = [
    {
      size: "XS",
      hasSize: product.sizeXS,
      stock: product.stockSizeXS,
      alert: product.stockAlertXS,
    },
    {
      size: "S",
      hasSize: product.sizeS,
      stock: product.stockSizeS,
      alert: product.stockAlertS,
    },
    {
      size: "M",
      hasSize: product.sizeM,
      stock: product.stockSizeM,
      alert: product.stockAlertM,
    },
    {
      size: "L",
      hasSize: product.sizeL,
      stock: product.stockSizeL,
      alert: product.stockAlertL,
    },
    {
      size: "XL",
      hasSize: product.sizeXL,
      stock: product.stockSizeXL,
      alert: product.stockAlertXL,
    },
    {
      size: "XXL",
      hasSize: product.sizeXXL,
      stock: product.stockSizeXXL,
      alert: product.stockAlertXXL,
    },
    {
      size: "3XL",
      hasSize: product.size3XL,
      stock: product.stockSize3XL,
      alert: product.stockAlert3XL,
    },
    {
      size: "4XL",
      hasSize: product.size4XL,
      stock: product.stockSize4XL,
      alert: product.stockAlert4XL,
    },
    {
      size: "5XL",
      hasSize: product.size5XL,
      stock: product.stockSize5XL,
      alert: product.stockAlert5XL,
    },
  ];

  sizeConfig.forEach(({ size, hasSize, stock, alert }) => {
    if (hasSize && stock >= 0) {
      // Show all sizes that are enabled, even if stock is 0
      const alertThreshold = alert || 10; // Default alert threshold
      let alertLevel = "normal";

      if (stock < alertThreshold) {
        alertLevel = "critical"; // Red
      } else if (stock < alertThreshold * 2) {
        alertLevel = "warning"; // Orange
      }

      stocks.push({
        size,
        stock,
        alertThreshold,
        alertLevel,
        displayText: `${size}: ${stock}`,
      });
    }
  });

  return stocks;
};

export const parseStockFromString = (stockString) => {
  // Parsira string tipa "S: 15" i vraća { size: 'S', stock: 15 }
  const match = stockString.match(/^(.+?):\s*(\d+)$/);
  if (match) {
    return {
      size: match[1].trim(),
      stock: parseInt(match[2], 10),
    };
  }
  return { size: stockString, stock: 0 };
};

// Slug za URL filtere (v2 redizajn): "Šolje" -> "solje", "Goblini" -> "goblini"
export const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/š/g, "s")
    .replace(/đ/g, "dj")
    .replace(/č/g, "c")
    .replace(/ć/g, "c")
    .replace(/ž/g, "z")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
